import * as React from 'react';
import {
  FunctionComponent,
  PropsWithChildren,
  ReactElement,
  useContext,
  useEffect,
} from 'react';

import { ThemeProvider } from '@mui/material';
import keys from 'lodash/keys';

import StyledFormWrapper from '../../../../../../shared/components/styled-form-wrapper/styled-form-wrapper';
import { useAppDispatch, useAppSelector } from '../../../../../../store/hooks';
import { ChangeDetectorContext } from '../../../../../shared/components/change-detector-context/ChangeDetectorContext';
import { ChangeDetectorContextModel } from '../../../../../shared/components/change-detector-context/models/change-detector-context.model';
import Box from '../../../../../shared/components/ui/Box';
import { useCheckPath } from '../../../../../shared/hooks/use-check-path/use-check-path.hook';
import { formTheme } from '../../../../../shared/themes/form-theme/form.theme';
import { usePopulateCampaignDetailsForm } from '../../../../hooks/use-populate-campaign-details-form/use-populate-campaign-details-form.hook';
import { setBasicInfo } from '../../../../store/slices/campaigns-basic-info.slice';
import { basicInfoResponseModelToFormik } from '../../../../utils/basic-info-response-model-to-formik/basic-info-response-model-to-formik.util';
import { BasicInfoFormikModel } from '../../models/basic-info-formik.model';
import { BasicInfoComponentProps } from '../../types/basic-info-component.props';

export const BasicInformationFormContainer: FunctionComponent<
  BasicInfoComponentProps
> = (props: PropsWithChildren<BasicInfoComponentProps>): ReactElement => {
  const {
    children,
    handleSubmit,
    dirty,
    touched,
    values,
    setValues,
    setTouched,
    validateForm,
    forceErrorsDisplay,
  } = props;

  const cdContext = useContext(
    ChangeDetectorContext
  ) as ChangeDetectorContextModel;

  const dispatch = useAppDispatch();

  const [isOnEdit, { id: campaignId }] = useCheckPath(
    '/campaign-management/edit/'
  );
  const data = useAppSelector(state => state.campaignsFeature.basicInfo.data);

  useEffect(() => {
    if (forceErrorsDisplay) {
      setTouched({
        name: true,
        startDate: true,
        endDate: true,
        wifiExpirationDate: true,
        maxRetryNo: true,
        version: true,
        firmwareIds: true,
        description: true,
        status: true,
      });
      validateForm();
    }
  }, [forceErrorsDisplay, validateForm]);

  useEffect(() => {
    dispatch(setBasicInfo(values));
  }, [values]);

  useEffect(() => {
    cdContext.setIsChanged(dirty && keys(touched).length > 0);
  }, [dirty, touched]);

  usePopulateCampaignDetailsForm<BasicInfoFormikModel>(
    isOnEdit,
    campaignId,
    setValues,
    basicInfoResponseModelToFormik,
    data
  );

  return (
    <ThemeProvider theme={formTheme}>
      <Box
        sx={{
          my: '48px',
        }}
        data-testid="campaign-basic-info"
      >
        <StyledFormWrapper onSubmit={handleSubmit}>
          {children}
        </StyledFormWrapper>
      </Box>
    </ThemeProvider>
  );
};
